import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import api from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Eye, Trash2, Car, Calendar, FileDown, Code, Loader2, Shield } from "lucide-react";
import MFCWLOverrideModal from "@/components/MFCWLOverrideModal";

export default function HistoryPage() {
  const [analyses, setAnalyses] = useState([]);
  const [loading, setLoading] = useState(true);

  // Modal states
  const [codeModalOpen, setCodeModalOpen] = useState(false);
  const [instructionModalOpen, setInstructionModalOpen] = useState(false);
  const [mfcwlOverrideOpen, setMfcwlOverrideOpen] = useState(false);
  const [selectedAnalysis, setSelectedAnalysis] = useState(null);
  const [codeInput, setCodeInput] = useState("");
  const [codeError, setCodeError] = useState("");
  const [instruction, setInstruction] = useState("");
  const [overrideLoading, setOverrideLoading] = useState(false);

  const fetchAnalyses = async () => {
    try {
      const r = await api.listSounds();
      setAnalyses(r.data || []);
    } catch {
      toast.error("Failed to load analysis history");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchAnalyses(); }, []);

  const handleDelete = async (id) => {
    try {
      await api.deleteSound(id);
      setAnalyses(prev => prev.filter(a => a.id !== id));
      toast.success("Analysis deleted");
    } catch {
      toast.error("Failed to delete");
    }
  };

  const handleDownloadPdf = (id) => {
    const url = api.getSoundPdfUrl(id);
    window.open(url, '_blank');
    toast.success("PDF download started");
  };

  // Code button flow
  const handleCodeClick = (analysis) => {
    setSelectedAnalysis(analysis);
    setCodeInput("");
    setCodeError("");
    setCodeModalOpen(true);
  };

  const handleCodeSubmit = () => {
    if (codeInput === "539953") {
      setCodeModalOpen(false);
      const isMfcwl = selectedAnalysis?.source === "mfcwl";
      if (isMfcwl) {
        // Fetch full analysis detail for the visual editor (list endpoint doesn't include sound data)
        api.getSoundDetail(selectedAnalysis.id).then(resp => {
          setSelectedAnalysis(resp.data);
          setMfcwlOverrideOpen(true);
        }).catch(() => {
          toast.error("Failed to load analysis details");
        });
      } else {
        setInstruction("");
        setInstructionModalOpen(true);
      }
    } else {
      setCodeError("Invalid code. Access denied.");
    }
  };

  const handleOverrideSubmit = async () => {
    if (!instruction.trim()) {
      toast.error("Please enter an instruction");
      return;
    }
    
    setOverrideLoading(true);
    try {
      const response = await api.smartOverride(selectedAnalysis.id, instruction, "539953");
      const result = response.data;
      
      toast.success(`Override applied: ${result.updates.join(", ")} updated`);
      setInstructionModalOpen(false);
      
      // Refresh the list to show updated data
      fetchAnalyses();
    } catch (err) {
      const msg = err.response?.data?.detail || err.response?.data?.error || err.message || "Override failed";
      toast.error(msg);
    } finally {
      setOverrideLoading(false);
    }
  };

  const getRatingColor = (grade) => {
    const map = { A: "bg-emerald-100 text-emerald-800 border-emerald-200", B: "bg-sky-100 text-sky-800 border-sky-200", C: "bg-amber-100 text-amber-800 border-amber-200", D: "bg-orange-100 text-orange-800 border-orange-200", F: "bg-red-100 text-red-800 border-red-200" };
    return map[grade] || "bg-slate-100 text-slate-800 border-slate-200";
  };

  return (
    <div className="space-y-5">
      <div>
        <h1 className="text-3xl md:text-4xl font-bold tracking-tight uppercase font-['Barlow'] text-slate-900" data-testid="history-title">
          Analysis History
        </h1>
        <p className="text-base text-slate-500 mt-1">Review past engine diagnostic reports</p>
      </div>

      <Card className="border border-slate-200 shadow-none rounded-sm overflow-hidden">
        <CardContent className="p-0">
          {loading ? (
            <div className="p-10 text-center text-base text-slate-400">Loading analyses...</div>
          ) : analyses.length === 0 ? (
            <div className="p-16 text-center" data-testid="empty-history">
              <Car size={48} className="mx-auto text-slate-300 mb-4" />
              <p className="text-base text-slate-400 font-medium">No analyses yet</p>
              <Link to="/">
                <Button className="mt-5 bg-slate-900 text-white rounded-sm uppercase tracking-wider font-bold text-sm px-6 py-3" data-testid="go-analyze-btn">
                  Run Your First Analysis
                </Button>
              </Link>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow className="bg-slate-50/80">
                  <TableHead className="text-sm font-bold uppercase tracking-wider text-slate-500 py-4">Vehicle</TableHead>
                  <TableHead className="text-sm font-bold uppercase tracking-wider text-slate-500 py-4">Source</TableHead>
                  <TableHead className="text-sm font-bold uppercase tracking-wider text-slate-500 py-4">Year</TableHead>
                  <TableHead className="text-sm font-bold uppercase tracking-wider text-slate-500 py-4">KM Driven</TableHead>
                  <TableHead className="text-sm font-bold uppercase tracking-wider text-slate-500 py-4">Rating</TableHead>
                  <TableHead className="text-sm font-bold uppercase tracking-wider text-slate-500 py-4">Date</TableHead>
                  <TableHead className="text-sm font-bold uppercase tracking-wider text-slate-500 py-4 text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {analyses.map((a) => {
                  const isMfcwl = a.source === "mfcwl";
                  const viewUrl = isMfcwl ? `/mfcwl?id=${a.id}` : `/analysis/${a.id}`;
                  return (
                  <TableRow key={a.id} className="hover:bg-slate-50/50 transition-colors" data-testid={`analysis-row-${a.id}`}>
                    <TableCell className="font-medium text-base py-4">
                      <div className="flex items-center gap-2">
                        <Car size={16} className="text-slate-400" />
                        {a.car_details?.make} {a.car_details?.model}
                      </div>
                    </TableCell>
                    <TableCell className="py-4">
                      <Badge className={`rounded-full text-xs font-bold uppercase tracking-widest border px-3 ${isMfcwl ? 'bg-violet-100 text-violet-800 border-violet-200' : 'bg-sky-100 text-sky-800 border-sky-200'}`}>
                        {isMfcwl ? "MFCWL" : "Sound AI"}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-base font-mono py-4">{a.car_details?.year}</TableCell>
                    <TableCell className="text-base font-mono py-4">{a.car_details?.km_driven?.toLocaleString()} km</TableCell>
                    <TableCell className="py-4">
                      <Badge className={`rounded-full text-xs font-bold uppercase tracking-widest border px-3 ${getRatingColor(a.engine_rating?.grade)}`}>
                        {a.engine_rating?.grade} - {a.engine_rating?.overall_rating}%
                      </Badge>
                    </TableCell>
                    <TableCell className="text-sm text-slate-500 font-mono py-4">
                      <div className="flex items-center gap-1">
                        <Calendar size={14} />
                        {new Date(a.created_at).toLocaleDateString()}
                      </div>
                    </TableCell>
                    <TableCell className="text-right py-4">
                      <div className="flex items-center gap-2 justify-end">
                        <Button 
                          size="sm" 
                          variant="ghost" 
                          className="h-9 w-9 p-0 text-indigo-500 hover:text-indigo-700 hover:bg-indigo-50" 
                          onClick={() => handleCodeClick(a)}
                          title="Override"
                          data-testid={`code-btn-${a.id}`}
                        >
                          <Code size={16} />
                        </Button>
                        <Button 
                          size="sm" 
                          variant="ghost" 
                          className="h-9 w-9 p-0 text-orange-500 hover:text-orange-700 hover:bg-orange-50" 
                          onClick={() => handleDownloadPdf(a.id)}
                          title="Download PDF Report"
                          data-testid={`download-pdf-${a.id}`}
                        >
                          <FileDown size={16} />
                        </Button>
                        <Link to={viewUrl}>
                          <Button size="sm" variant="ghost" className="h-9 w-9 p-0" data-testid={`view-analysis-${a.id}`}>
                            <Eye size={16} />
                          </Button>
                        </Link>
                        <Button size="sm" variant="ghost" className="h-9 w-9 p-0 text-red-500 hover:text-red-700" onClick={() => handleDelete(a.id)} data-testid={`delete-analysis-${a.id}`}>
                          <Trash2 size={16} />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Code Entry Modal */}
      <Dialog open={codeModalOpen} onOpenChange={setCodeModalOpen}>
        <DialogContent className="sm:max-w-md" data-testid="code-modal">
          <DialogHeader>
            <DialogTitle className="text-lg font-bold uppercase tracking-wider">Enter Access Code</DialogTitle>
            <DialogDescription className="text-sm text-slate-500">
              Enter the authorized code to modify this analysis
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <Input
              type="password"
              placeholder="Enter code..."
              value={codeInput}
              onChange={(e) => { setCodeInput(e.target.value); setCodeError(""); }}
              onKeyDown={(e) => e.key === "Enter" && handleCodeSubmit()}
              className="text-center text-lg font-mono tracking-widest"
              data-testid="code-input"
              autoFocus
            />
            {codeError && (
              <p className="text-sm text-red-500 text-center font-medium" data-testid="code-error">{codeError}</p>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCodeModalOpen(false)}>Cancel</Button>
            <Button onClick={handleCodeSubmit} className="bg-slate-900 text-white" data-testid="code-submit-btn">
              Verify
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Instruction Modal */}
      <Dialog open={instructionModalOpen} onOpenChange={setInstructionModalOpen}>
        <DialogContent className="sm:max-w-lg" data-testid="instruction-modal">
          <DialogHeader>
            <DialogTitle className="text-lg font-bold uppercase tracking-wider">Override Analysis</DialogTitle>
            <DialogDescription className="text-sm text-slate-500">
              {selectedAnalysis && (
                <span className="font-medium text-slate-700">
                  {selectedAnalysis.car_details?.make} {selectedAnalysis.car_details?.model} ({selectedAnalysis.car_details?.year}) — Current Rating: {selectedAnalysis.engine_rating?.overall_rating}%
                </span>
              )}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <Textarea
              placeholder="Enter your instruction... e.g. 'Change engine rating to 82, add knocking sound as elevated, re-analyse'"
              value={instruction}
              onChange={(e) => setInstruction(e.target.value)}
              className="min-h-[120px] text-sm"
              data-testid="instruction-input"
              autoFocus
            />
            <div className="bg-slate-50 border border-slate-200 rounded p-3">
              <p className="text-xs text-slate-400 font-bold uppercase tracking-wider mb-1">Examples:</p>
              <ul className="text-xs text-slate-500 space-y-1">
                <li>• "Change engine rating to 75"</li>
                <li>• "Add knocking sound as critical, reduce rating to 65"</li>
                <li>• "Change estimated value to RM 160,350"</li>
                <li>• "Set bank asset quality to 80 grade A, max LTV 85%"</li>
                <li>• "Update insurability score to 70, reduce NPA risk to 3%"</li>
                <li>• "Change AI health score to 90, re-analyse"</li>
              </ul>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setInstructionModalOpen(false)} disabled={overrideLoading}>
              Cancel
            </Button>
            <Button 
              onClick={handleOverrideSubmit} 
              className="bg-indigo-600 text-white hover:bg-indigo-700"
              disabled={overrideLoading || !instruction.trim()}
              data-testid="override-submit-btn"
            >
              {overrideLoading ? (
                <><Loader2 size={14} className="mr-2 animate-spin" /> Processing...</>
              ) : (
                "Apply Override"
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {/* MFCWL Visual Override Modal */}
      <MFCWLOverrideModal
        open={mfcwlOverrideOpen}
        onOpenChange={setMfcwlOverrideOpen}
        analysis={selectedAnalysis}
        onSuccess={fetchAnalyses}
      />
    </div>
  );
}
